import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Check } from 'lucide-react';
import useConfiguratorStore from '../store/configuratorStore';
import QuantitySelector from './QuantitySelector';
import { getProductImageSrc } from '../utils/imageUtils';

// Define EnlargeButton first so it can be referenced in other styled components
const EnlargeButton = styled(motion.button)`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }
`;

const Section = styled.div`
  margin-bottom: 2.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: ${props => props.$isExpanded ? '1.5rem' : '0'};
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--gray-200);
  border-radius: var(--rounded-md);
  padding: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  background: ${props => props.$highlighted ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%)' : 'transparent'};

  &:hover {
    background: ${props => props.$highlighted ?
      'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.05) 100%)' :
      'var(--gray-50)'};
  }
`;

const SectionNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => {
    if (props.$completed) return 'var(--success-color, #22c55e)';
    return 'var(--primary-color)';
  }};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: var(--text-md);
  transition: all 0.3s ease;
`;

const SectionTitle = styled.h2`
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--secondary-color);
  flex: 1;
`;

const ChevronIcon = styled(ChevronRight)`
  width: 24px;
  height: 24px;
  color: var(--gray-500);
  transition: transform 0.3s ease;
  transform: ${props => props.$isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const AccordionContent = styled(motion.div)`
  overflow: visible;
`;

const DeckGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const DeckCard = styled(motion.div)`
  cursor: pointer;
  background-color: white;
  border-radius: var(--rounded-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  border: 2px solid ${props => props.selected ? 'var(--primary-color)' : 'var(--gray-200)'};
  transition: all 0.2s ease;
  position: relative;
  height: 220px;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
    border-color: ${props => props.selected ? 'var(--primary-color)' : 'var(--gray-300)'};
  }
`;

const DeckImageContainer = styled.div`
  height: 140px;
  background-color: var(--gray-50);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  border-bottom: 1px solid var(--gray-200);
  position: relative;
  overflow: hidden;
  
  &:hover ${EnlargeButton} {
    opacity: 1;
  }
`;

const DeckImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
  
  img[src="https://i.imgur.com/EcwVKFI.png"] {
    transform: scale(0.55); /* 45% smaller */
  }

  /* Make the Food Grade No Slide image smaller */
  img[src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images/Food%20Grade%20No%20Slide.avif"] {
    transform: scale(0.4); /* 60% smaller */
  }

  /* Make the Standard No Slide image smaller */
  img[src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images/Deck%20No%20Slide.avif"] {
    transform: scale(0.4); /* 60% smaller */
  }

  ${DeckCard}:hover & img {
    transform: scale(1.05);
  }

  ${DeckCard}:hover & img[src="https://i.imgur.com/EcwVKFI.png"] {
    transform: scale(0.6);
  }

  /* Adjust hover scale for Food Grade No Slide */
  ${DeckCard}:hover & img[src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images/Food%20Grade%20No%20Slide.avif"] {
    transform: scale(0.42); /* Slight scale on hover */
  }

  /* Adjust hover scale for Standard No Slide */
  ${DeckCard}:hover & img[src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images/Deck%20No%20Slide.avif"] {
    transform: scale(0.42); /* Slight scale on hover */
  }
`;

const BadgeContainer = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  align-items: flex-end;
`;

const Badge = styled.span`
  background-color: ${props => {
    if (props.type === 'food') return 'var(--success-light)';
    if (props.type === 'stainless') return '#e0f2ff';
    if (props.type === 'open') return '#fff8e0';
    if (props.type === 'noslide') return '#ffe0e0';
    return 'var(--gray-200)';
  }};
  color: ${props => {
    if (props.type === 'food') return 'var(--success-color)';
    if (props.type === 'stainless') return '#0077cc';
    if (props.type === 'open') return '#cc7700';
    if (props.type === 'noslide') return '#cc0000';
    return 'var(--gray-700)';
  }};
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: var(--rounded-sm);
  white-space: nowrap;
`;

const DeckInfo = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
`;

const DeckName = styled.h3`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--gray-800);
  margin-bottom: 0.25rem;
  line-height: 1.3;
`;

const DeckPrice = styled.div`
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--primary-color);
  margin-top: auto;
  
  span {
    font-size: 0.85em;
    opacity: 0.7;
    margin-right: 4px;
  }
`;

const SelectedCheck = styled(motion.div)`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  z-index: 5;
`;

const QuickAddButton = styled(motion.button)`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.added ? '#22c55e' : '#16a34a'};
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  z-index: 4;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  transition: all 0.4s ease;
  opacity: ${props => props.hovered ? 1 : 0.8};
  line-height: 1;
  text-align: center;
  
  &:hover {
    background: #15803d;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ProductSummary = styled.div`
  background-color: white;
  border-radius: var(--rounded-lg);
  box-shadow: var(--shadow-md);
  margin-top: 2rem;
  margin-bottom: 2rem;
  overflow: hidden;
`;

const SummaryHeader = styled.div`
  background-color: var(--gray-50);
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--gray-200);
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SummaryTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--secondary-color);
`;

const SummaryContent = styled.div`
  padding: 1.5rem;
`;

const ProductDetails = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
`;

const ProductImage = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  background-color: var(--gray-50);
  border-radius: var(--rounded-md);
  padding: 0.5rem;
  border: 1px solid var(--gray-200);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover ${EnlargeButton} {
    opacity: 1;
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  img[src="https://i.imgur.com/EcwVKFI.png"] {
    transform: scale(0.55); /* 45% smaller */
  }
`;

const ProductInfo = styled.div`
  flex: 1;
  padding-left: 1.5rem;
`;

const ProductName = styled.h4`
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
`;

const ProductCode = styled.div`
  font-size: var(--text-xs);
  color: var(--gray-600);
  background-color: var(--gray-100);
  padding: 0.25rem 0.5rem;
  border-radius: var(--rounded-sm);
  display: inline-block;
  margin-bottom: 0.5rem;
`;

const FeatureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Feature = styled.span`
  font-size: var(--text-xs);
  color: var(--gray-700);
  background-color: var(--gray-100);
  padding: 0.25rem 0.5rem;
  border-radius: var(--rounded-sm);
  display: inline-flex;
  align-items: center;
  
  &:before {
    content: '•';
    margin-right: 0.25rem;
    color: var(--primary-color);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--gray-500);
`;

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

// Extract deck features from the name
const extractDeckFeatures = (name) => {
  const features = [];
  
  if (name.includes('Standard')) features.push('Standard');
  if (name.includes('Food Grade')) features.push('Food Grade');
  if (name.includes('Stainless Steel')) features.push('Stainless Steel');
  if (name.includes('open sides')) features.push('Open Sides');
  if (name.includes('No Slide')) features.push('No Slide');
  if (name.includes('Powder Unloading Station')) features.push('Unloading Station Compatible');
  
  return features;
};

// Image Modal Components
const ImageModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(8px);
`;

const ImageModalContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
`;

const FullscreenImage = styled.img`
  width: 100%;
  height: 100%;
  max-width: 80vw;
  max-height: 80vh;
  object-fit: contain;
  display: block;
`;

const ImageModalHeader = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  padding: 2rem 1.5rem 1.5rem;
  text-align: center;
`;

const ImageModalTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  margin: 0 0 0.5rem;
`;

const ImageModalCode = styled.p`
  font-size: var(--text-sm);
  opacity: 0.8;
  margin: 0;
`;

const ImageModalCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }
`;

function DeckConfigurator() {
  const {
    organizedProducts,
    selectedMainCategory,
    selectedProduct,
    setSelectedProduct,
    quickAddToQuote,
    calculateQuote,
    priceMode
  } = useConfiguratorStore();

  const [deckProducts, setDeckProducts] = useState([]);
  const [foodGradeDecks, setFoodGradeDecks] = useState([]);
  const [standardDecks, setStandardDecks] = useState([]);
  const [quickAddStates, setQuickAddStates] = useState({});
  const [hoveredCard, setHoveredCard] = useState(null);
  const [imageModalData, setImageModalData] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set([1, 2])); // Start with both Standard and Food Grade Decks expanded

  // Ref for auto-scrolling to product summary
  const productSummaryRef = useRef(null);
  
  // Initialize and group deck products
  useEffect(() => {
    if (selectedMainCategory === 'decks' && organizedProducts?.decks) {
      const allDecks = organizedProducts.decks;
      setDeckProducts(allDecks);
      
      // Group products by category
      const foodGrade = allDecks.filter(deck => deck.name.includes('Food Grade'));
      const standard = allDecks.filter(deck => !deck.name.includes('Food Grade'));
      
      setFoodGradeDecks(foodGrade);
      setStandardDecks(standard);
    }
  }, [selectedMainCategory, organizedProducts]);
  
  // Re-render when price mode changes
  useEffect(() => {
    // This empty effect will trigger re-render when priceMode changes
  }, [priceMode]);
  
  // Get selected product details
  const productObject = selectedProduct && deckProducts.find(p => p.id === selectedProduct);
  
  // Handle deck selection
  const handleDeckSelect = (deckId) => {
    setSelectedProduct(deckId);
    calculateQuote();

    // Auto-expand Food Grade section when Standard Deck is selected
    const isStandardDeck = standardDecks.some(deck => deck.id === deckId);
    if (isStandardDeck && !expandedSections.has(2)) {
      setExpandedSections(prev => new Set([...prev, 2]));
    }
  };

  // Handle quick add to quote
  const handleQuickAdd = (deck, e) => {
    e.stopPropagation(); // Prevent card selection
    
    // Set quick add state for visual feedback
    setQuickAddStates(prev => ({
      ...prev,
      [deck.id]: true
    }));
    
    // Call the new incremental quick add function
    quickAddToQuote(deck.id, selectedMainCategory);
    
    // Reset quick add state after animation
    setTimeout(() => {
      setQuickAddStates(prev => ({
        ...prev,
        [deck.id]: false
      }));
    }, 1500);
  };
  
  // Image modal handlers
  const handleOpenImageModal = (imageSrc, productName, productId) => {
    setImageModalData({
      imageSrc,
      productName,
      productId
    });
  };
  
  const handleCloseImageModal = () => {
    setImageModalData(null);
  };

  // Accordion toggle function
  const toggleSection = (sectionNumber) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionNumber)) {
        newSet.delete(sectionNumber);
      } else {
        newSet.add(sectionNumber);
      }
      return newSet;
    });
  };

  // Check if a section has a selected product
  const sectionHasSelection = (decks) => {
    return decks.some(deck => deck.id === selectedProduct);
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && imageModalData) {
        handleCloseImageModal();
      }
    };

    if (imageModalData) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [imageModalData]);

  // Auto-scroll to product summary when a product is selected
  useEffect(() => {
    if (selectedProduct && productSummaryRef.current) {
      // Small delay to ensure DOM has rendered
      setTimeout(() => {
        productSummaryRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 300);
    }
  }, [selectedProduct]);

  // Render a section of deck products
  const renderDeckSection = (decksToRender, title, step) => {
    const isExpanded = expandedSections.has(step);
    const hasSelection = sectionHasSelection(decksToRender);

    return (
      <Section>
        <SectionHeader
          onClick={() => toggleSection(step)}
          $isExpanded={isExpanded}
          $highlighted={hasSelection}
        >
          <SectionNumber
            $completed={hasSelection}
          >
            {hasSelection ? <Check size={18} /> : step}
          </SectionNumber>
          <SectionTitle>{title}</SectionTitle>
          <ChevronIcon $isExpanded={isExpanded} />
        </SectionHeader>

        <AnimatePresence initial={false}>
          {isExpanded && (
            <AccordionContent
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <DeckGrid>
          {decksToRender.map(deck => {
            const isSelected = selectedProduct === deck.id;
            const deckFeatures = extractDeckFeatures(deck.name);
            
            // Get partner or client price based on current mode
            const getPrice = () => {
              if (!deck.quantities || deck.quantities.length === 0) {
                return deck.basePrice;
              }
              
              // Get first quantity tier price based on price mode
              const firstTier = deck.quantities[0];
              if (priceMode === 'partner') {
                return firstTier.partnerPrice;
              } else {
                return firstTier.clientPrice;
              }
            };
            
            const price = getPrice();
            const imageSrc = getProductImageSrc(deck, 'decks');
            
            return (
              <DeckCard 
                key={deck.id}
                selected={isSelected}
                onClick={() => handleDeckSelect(deck.id)}
                onMouseEnter={() => setHoveredCard(deck.id)}
                onMouseLeave={() => setHoveredCard(null)}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.98 }}
              >
                <DeckImageContainer>
                  <DeckImage>
                    <img src={imageSrc} alt={deck.name} />
                  </DeckImage>
                  <EnlargeButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenImageModal(imageSrc, deck.name, deck.id);
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ⛶
                  </EnlargeButton>
                  <BadgeContainer>
                    {deck.name.includes('Food Grade') && (
                      <Badge type="food">Food Grade</Badge>
                    )}
                    {deck.name.includes('Stainless Steel') && (
                      <Badge type="stainless">Stainless Steel</Badge>
                    )}
                    {deck.name.includes('open sides') && (
                      <Badge type="open">Open Sides</Badge>
                    )}
                    {deck.name.includes('No Slide') && (
                      <Badge type="noslide">No Slide</Badge>
                    )}
                  </BadgeContainer>
                  {isSelected && (
                    <SelectedCheck 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring" }}
                    >
                      ✓
                    </SelectedCheck>
                  )}
                </DeckImageContainer>
                
                <DeckInfo>
                  <DeckName>{deck.name.replace('Neva Deck | ', '')}</DeckName>
                  <DeckPrice>
                    <span>{priceMode === 'partner' ? 'Partner' : 'Client'}:</span>
                    {formatCurrency(price)}
                  </DeckPrice>
                  <QuickAddButton
                    hovered={hoveredCard === deck.id}
                    added={quickAddStates[deck.id]}
                    onClick={(e) => handleQuickAdd(deck, e)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0.8 }}
                    animate={{ 
                      opacity: hoveredCard === deck.id ? 1 : 0.8,
                      scale: quickAddStates[deck.id] ? 1.15 : 1
                    }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                  >
                    +
                  </QuickAddButton>
                </DeckInfo>
              </DeckCard>
            );
          })}
              </DeckGrid>
            </AccordionContent>
          )}
        </AnimatePresence>
      </Section>
    );
  };
  
  // If not viewing decks, don't render
  if (selectedMainCategory !== 'decks' || !deckProducts.length) {
    return <EmptyState>Please select the Decks category to view options</EmptyState>;
  }
  
  return (
    <div>
      {renderDeckSection(standardDecks, "Standard Decks", 1)}
      {renderDeckSection(foodGradeDecks, "Food Grade Decks", 2)}
      
      {/* Product Summary */}
      <AnimatePresence>
        {selectedProduct && productObject && (
          <motion.div
            ref={productSummaryRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ProductSummary>
              <SummaryHeader>
                <SummaryTitle>📦 Product Summary</SummaryTitle>
              </SummaryHeader>
              <SummaryContent>
                <ProductDetails>
                  <ProductImage>
                    <img src={getProductImageSrc(productObject, 'decks')} alt={productObject.name} />
                    <EnlargeButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenImageModal(getProductImageSrc(productObject, 'decks'), productObject.name, productObject.id);
                      }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      ⛶
                    </EnlargeButton>
                  </ProductImage>
                  <ProductInfo>
                    <ProductName>{productObject.name}</ProductName>
                    <ProductCode>Item #{productObject.id}</ProductCode>
                    <FeatureList>
                      {extractDeckFeatures(productObject.name).map((feature, index) => (
                        <Feature key={index}>{feature}</Feature>
                      ))}
                    </FeatureList>
                  </ProductInfo>
                </ProductDetails>
                <QuantitySelector />
              </SummaryContent>
            </ProductSummary>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Image Modal */}
      <AnimatePresence>
        {imageModalData && (
          <ImageModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseImageModal}
          >
            <ImageModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 15, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ImageModalCloseButton onClick={handleCloseImageModal}>
                ×
              </ImageModalCloseButton>
              
              {typeof imageModalData.imageSrc === 'string' && imageModalData.imageSrc.startsWith('http') ? (
                <FullscreenImage 
                  src={imageModalData.imageSrc} 
                  alt={imageModalData.productName}
                />
              ) : (
                <div style={{ 
                  padding: '4rem', 
                  fontSize: '4rem', 
                  textAlign: 'center',
                  background: 'white'
                }}>
                  {imageModalData.imageSrc}
                </div>
              )}
              
              <ImageModalHeader>
                <ImageModalTitle>{imageModalData.productName}</ImageModalTitle>
                <ImageModalCode>Item #{imageModalData.productId}</ImageModalCode>
              </ImageModalHeader>
            </ImageModalContent>
          </ImageModalOverlay>
        )}
      </AnimatePresence>
    </div>
  );
}

export default DeckConfigurator;
